import {
  STATUSPAGE,
  TableWithMovableRows,
  MainButton,
  ModalConfirmAction,
} from '@repo/ui';
import {
  ChangeSemesterOrderUseCaseInterface,
  DeleteSemesterUseCaseInterface,
  GetAllSemestersFromEntityUseCaseInterface,
  GetSemesterByIdUseCaseInterface,
} from 'domain/usecases';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { makeSemestersFromEntityListColumns } from 'presentation/app/modules/Entities/common/columns/entity-settings/semesters-from-entity-list-columns';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearSemestersFromEntityListData,
  loadSemestersFromEntityListData,
  loadUpdateSemesterModalData,
} from 'presentation/config/store/entities/entities-slice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Card, CardBody } from 'reactstrap';

import noDataImgSrc from '../../../../../../assets/images/no-task.png';
import { CreateOrUpdateSemesterModal } from './create-or-update-semester-modal';

interface IProps {
  entityId: string;
}

export const SemestersTabContent = ({ entityId }: IProps) => {
  // Hooks
  const { entitiesService } = useService();
  const dispatch = useDispatch();

  // States
  const { semestersFromEntityListData } = useAppSelector(
    (state) => state.Entities,
  );
  const selectedSemesterIdRef = useRef<string | null>(undefined);
  const [
    isCreateOrUpdateSemesterModalOpen,
    setIsCreateOrUpdateSemesterModalOpen,
  ] = useState(false);
  const [isDeleteSemesterModalOpen, setIsDeleteSemesterModalOpen] =
    useState(false);
  const [
    requestGetAllSemestersFromEntityStatus,
    setRequestGetAllSemestersFromEntityStatus,
  ] = useState(STATUSPAGE.DONE);
  const [
    requestDeleteSemesterFromEntityStatus,
    setRequestDeleteSemesterFromEntityStatus,
  ] = useState(STATUSPAGE.DONE);
  const [requestGetSemesterByIdStatus, setRequestGetSemesterByIdStatus] =
    useState(STATUSPAGE.DONE);

  const noDataConfig = {
    noDataTitle: 'Nenhum semestre encontrado',
    noDataDescription: 'Não há semestres cadastrados no momento.',
  };

  // Methods
  const toggleCreateOrUpdateSemesterModal = useCallback(() => {
    setIsCreateOrUpdateSemesterModalOpen((prevState) => !prevState);
  }, []);

  const toggleIsDeleteSemesterModalOpen = useCallback(() => {
    setIsDeleteSemesterModalOpen((prevState) => !prevState);
  }, []);

  const requestGetAllSemestersFromEntity = useCallback(
    async (params: GetAllSemestersFromEntityUseCaseInterface.Params) => {
      setRequestGetAllSemestersFromEntityStatus(STATUSPAGE.LOADING);
      await entitiesService
        .getAllSemestersFromEntity(params)
        .then((res) => {
          dispatch(loadSemestersFromEntityListData(res));
        })
        .catch((err) => {
          notifyError(err.message);
          dispatch(clearSemestersFromEntityListData());
        })
        .finally(() => {
          setRequestGetAllSemestersFromEntityStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestGetSemesterById = useCallback(
    async (params: GetSemesterByIdUseCaseInterface.Params) => {
      setRequestGetSemesterByIdStatus(STATUSPAGE.LOADING);
      await entitiesService
        .getSemesterById(params)
        .then((res) => {
          dispatch(loadUpdateSemesterModalData(res));
          toggleCreateOrUpdateSemesterModal();
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setRequestGetSemesterByIdStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestChangeSemesterOrder = useCallback(
    async (params: ChangeSemesterOrderUseCaseInterface.Params) => {
      await entitiesService
        .changeSemesterOrder(params)
        .then((res) => {
          notifySuccess(res.message);
        })
        .catch((err) => {
          notifyError(err.message);
          requestGetAllSemestersFromEntity({ entityId });
        });
    },
    [],
  );

  const requestDeleteSemester = useCallback(
    async (params: DeleteSemesterUseCaseInterface.Params) => {
      setRequestDeleteSemesterFromEntityStatus(STATUSPAGE.LOADING);
      await entitiesService
        .deleteSemester(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllSemestersFromEntity({ entityId });
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          toggleIsDeleteSemesterModalOpen();
          setRequestDeleteSemesterFromEntityStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  useEffect(() => {
    requestGetAllSemestersFromEntity({ entityId });
  }, []);

  return (
    <Card className="mt-5">
      <CardBody>
        <MainButton
          className="mb-3"
          onClick={() => toggleCreateOrUpdateSemesterModal()}
        >
          Novo
        </MainButton>

        <CreateOrUpdateSemesterModal
          isOpen={isCreateOrUpdateSemesterModalOpen}
          entityId={entityId}
          selectedSemesterIdRef={selectedSemesterIdRef}
          toggle={toggleCreateOrUpdateSemesterModal}
          requestGetAllSemestersFromEntity={requestGetAllSemestersFromEntity}
        />
        <TableWithMovableRows
          noDataTitle={noDataConfig.noDataTitle}
          noDataDescription={noDataConfig.noDataDescription}
          data={semestersFromEntityListData || []}
          areRowsDraggable={false}
          columns={makeSemestersFromEntityListColumns()}
          requestReorder={({ id, newOrder }) =>
            requestChangeSemesterOrder({ id: String(id), entityId, newOrder })
          }
          isLoading={
            requestGetAllSemestersFromEntityStatus === STATUSPAGE.LOADING
          }
          showMoveUpAndDownBtns
          showOrderColumn
          handleEdit={(id) => {
            selectedSemesterIdRef.current = id.toString();
            requestGetSemesterById({ id: String(id) });
          }}
          handleDelete={(id) => {
            selectedSemesterIdRef.current = id.toString();
            toggleIsDeleteSemesterModalOpen();
          }}
        />

        <ModalConfirmAction
          modalTheme="danger"
          isOpen={isDeleteSemesterModalOpen}
          toggle={toggleIsDeleteSemesterModalOpen}
          onClosed={() => {
            selectedSemesterIdRef.current = null;
          }}
          onConfirm={() =>
            requestDeleteSemester({
              id: selectedSemesterIdRef.current.toString(),
            })
          }
          title="Remover Semestre"
          subtitle="Ao remover este semestre, todos os grupos e estudantes vinculados a ele serão desvinculados. Tem certeza de que deseja continuar?"
          loading={requestDeleteSemesterFromEntityStatus === STATUSPAGE.LOADING}
          actionBtnLabel="Remover"
        />
      </CardBody>
    </Card>
  );
};
